/**
 * RefinePanel — "make changes and run again" affordance for the AI
 * drafting tools (second-round edits).
 *
 * Reusable across the Email Helper (tool page + drafts detail page) and
 * the Assessment Drafter. The owning page supplies the mutation: this
 * component only collects the change request, fires onRefine, and shows
 * the pending / error / restore states. Plain staff-facing copy, no
 * engine-speak (house R1/R2 language rules).
 *
 * The restore link is the "keep prior text recoverable" mechanism: the
 * owning page holds the pre-refine text in state and swaps it back. It is
 * client-side only (nothing is persisted until the operator saves), so a
 * saved draft is never clobbered by a refine the operator walks away from.
 */

import { type ReactNode, useState } from 'react';
import { Button } from '@/components/primitives/Button';
import { TextTooLongNote, isTextTooLong } from './TextTooLongNote';

export interface RefinePanelProps {
  /** Fires with the trimmed change request; the page owns the mutation. */
  onRefine: (instruction: string) => void;
  /** True while the refine call is in flight (disables the controls). */
  pending: boolean;
  /** Page-mapped error copy, or null. Rendered as a role=alert row. */
  error: string | null;
  /** Restore the pre-refine text (rendered only when available). */
  onRestore?: () => void;
  spice?: boolean;
  /** The longest change request the page's tool takes (characters, after
   *  trimming), read by the page from its tenant config. Past it the
   *  sentence shows under the box and the run button goes off. */
  maxChars: number;
  /** data-testid prefix so multiple panels stay addressable in tests. */
  testIdPrefix?: string;
}

const LABEL_CLS = 'block text-[0.8125rem] font-semibold text-sage-800';
const FIELD_CLS =
  'mt-1.5 block w-full min-h-11 rounded-[12px] border border-sage-400/20 bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';

export function RefinePanel({
  onRefine,
  pending,
  error,
  onRestore,
  testIdPrefix = 'refine',
  maxChars,
}: RefinePanelProps): ReactNode {
  const [instruction, setInstruction] = useState('');

  const trimmed = instruction.trim();
  const tooLong = isTextTooLong(instruction, maxChars);

  const handleRun = (): void => {
    if (trimmed.length === 0 || tooLong || pending) return;
    onRefine(trimmed);
    // Keep the text in the box while the call runs; clear it on the next
    // render only if the page signals success by re-mounting or the
    // operator edits it. Clearing here would lose the request on error.
  };

  return (
    <section
      className="rounded-[12px] border border-sage-800/[8%] bg-paper p-4 space-y-3"
      data-testid={`${testIdPrefix}-panel`}
    >
      <div>
        <label htmlFor={`${testIdPrefix}-instruction`} className={LABEL_CLS}>
          Want changes?
        </label>
        <p className="mt-0.5 text-xs leading-relaxed text-ink-soft">
          Say what to change and run it again. Your own edits stay unless you ask to change them.
        </p>
        <textarea
          id={`${testIdPrefix}-instruction`}
          className={FIELD_CLS}
          rows={2}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="Example: make it shorter, and mention the Tuesday visit"
          disabled={pending}
          data-testid={`${testIdPrefix}-instruction`}
        />
        {/* email_refine and assessment_refine say this in one shared sentence:
            a change request is a short box, shortened. */}
        {tooLong && (
          <TextTooLongNote
            surface="email_refine"
            maxChars={maxChars}
            kind="shorten"
            text={instruction}
            data-testid={`${testIdPrefix}-instruction-too-long`}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRun}
          loading={pending}
          disabled={pending || trimmed.length === 0 || tooLong}
          data-testid={`${testIdPrefix}-run`}
        >
          {pending ? 'Working on it…' : 'Make changes and run again'}
        </Button>
        {onRestore && !pending && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRestore}
            data-testid={`${testIdPrefix}-restore`}
          >
            Restore the previous version
          </Button>
        )}
      </div>

      {error !== null && (
        <p
          className="rounded-[12px] border border-warm-200 bg-warm-50 px-3 py-2 text-sm text-warm-700"
          role="alert"
          data-testid={`${testIdPrefix}-error`}
        >
          {error}
        </p>
      )}
    </section>
  );
}
